import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import {
  CircleCheckBig, SquareX, Upload, CirclePlus, Trash2,
  FileSignature, Users, FileArchive, Type as TypeIcon, FileText, ChevronDown, Check
} from "lucide-react";
import axiosInstance from "../../services/axiosInstance";
import styles from "./Modal.module.css"; // Certifique-se que este arquivo contém as classes do FormCriarMateria
import TiptapEditor from "../TiptapEditor";
import TransicaoComLoading from "../TransicaoComLoading";

const ANEXO_TIPOS_SUGESTOES = [
  "Documento Original", "Projeto de Lei", "Projeto de Resolução", "Resolução",
  "Projeto de Decreto Legislativo", "Decreto Legislativo", "Moção de Aplausos",
  "Aprovado", "Rejeitado", "Parecer da Comissão", "Diário Oficial", "Lei",
  "Veto", "Sanção", "Emenda", "Outros"
];

const ModalAdicionarAnexo = ({ isOpen, closeModal, pautaId, onSave, comissoes = [] }) => {
  const [loading, setLoading] = useState(false);
  
  // Controle de qual dropdown de tipo está aberto (pelo índice do array de anexos)
  const [tipoDropdownOpenIndex, setTipoDropdownOpenIndex] = useState(null);

  const [anexos, setAnexos] = useState([
    { 
      file: null, 
      tipo: "", 
      comissaoIds: [], 
      modo: "arquivo", 
      texto: "", 
      tituloTexto: "", 
      nomeDesejado: "" 
    },
  ]);

  // Fecha dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = () => setTipoDropdownOpenIndex(null);
    window.addEventListener("click", handleClickOutside);
    return () => window.removeEventListener("click", handleClickOutside);
  }, []);

  const handleAnexoChange = (index, field, value) => {
    const novos = [...anexos];
    novos[index][field] = value;
    
    // Limpezas automáticas ao trocar de modo
    if (field === "modo") {
        if (value === "arquivo") {
            novos[index].texto = "";
            novos[index].tituloTexto = "";
        } else {
            novos[index].file = null;
            novos[index].nomeDesejado = "";
        }
    }
    setAnexos(novos);
  };

  const handleFileChange = (index, file) => {
    const novos = [...anexos];
    novos[index].file = file;
    if (file?.name) {
      // Sugere o nome do arquivo sem a extensão
      novos[index].nomeDesejado = file.name.replace(/\.[^/.]+$/, "");
    }
    setAnexos(novos);
  };

  const addAnexoForm = () => {
    setAnexos([...anexos, { file: null, tipo: "", comissaoIds: [], modo: "arquivo", texto: "", tituloTexto: "", nomeDesejado: "" }]);
  };

  const removeAnexoForm = (index) => {
    setAnexos(anexos.filter((_, i) => i !== index));
  };

  const toggleComissao = (anexoIndex, comissaoId) => {
      const novos = [...anexos];
      const atuais = novos[anexoIndex].comissaoIds;
      if (atuais.includes(comissaoId)) {
          novos[anexoIndex].comissaoIds = atuais.filter(id => id !== comissaoId);
      } else {
          novos[anexoIndex].comissaoIds = [...atuais, comissaoId];
      }
      setAnexos(novos);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Validação básica
    const temConteudo = anexos.some(a => (a.modo === 'arquivo' && a.file) || (a.modo === 'texto' && a.texto));
    if (!temConteudo) return toast.warn("Adicione ao menos um arquivo ou texto.");

    setLoading(true);

    const fd = new FormData();
    const tipos = [];
    const comissoesArr = [];
    const nomes = [];
    const textos = [];

    anexos.forEach((ax) => {
      // Ignora itens vazios
      if (ax.modo === 'arquivo' && !ax.file) return;
      if (ax.modo === 'texto' && !ax.texto?.trim()) return;

      const tipoFinal = ax.tipo.trim() || "Anexo";
      
      if (ax.modo === "arquivo") {
        fd.append("arquivos", ax.file);
        tipos.push(tipoFinal);
        // Lógica: se for Parecer, manda array de IDs, senão null
        comissoesArr.push(tipoFinal.toLowerCase().includes("parecer") && ax.comissaoIds.length ? ax.comissaoIds : [null]);
        nomes.push(ax.nomeDesejado.trim() || null);
      } else {
        textos.push({
          tipo: tipoFinal,
          comissaoIds: tipoFinal.toLowerCase().includes("parecer") && ax.comissaoIds.length ? ax.comissaoIds : [null],
          conteudoTexto: ax.texto,
          descricao: ax.tituloTexto?.trim() || null,
        });
      }
    });

    fd.append("tipos", JSON.stringify(tipos));
    fd.append("comissoes", JSON.stringify(comissoesArr));
    fd.append("nomes", JSON.stringify(nomes));
    fd.append("textos", JSON.stringify(textos));

    try {
      await axiosInstance.post(`/anexos-pauta/${pautaId}/anexar`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success("Anexos salvos com sucesso!");
      if(onSave) onSave();
      closeModal();
    } catch (error) {
      console.error(error);
      toast.error("Erro ao enviar anexos.");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={styles.modalOverlay} onClick={closeModal}>
      <div 
        className={styles.modal} 
        style={{ display: 'flex', flexDirection: 'column', }} 
        onClick={(e) => e.stopPropagation()}
      >
        {loading ? <TransicaoComLoading /> : (
            <>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem'}}>
                    <h2 className={styles.modalTitle} style={{margin:0}}>Adicionar Anexos</h2>
                    <button className={styles.modalClose} onClick={closeModal}><SquareX /></button>
                </div>

                <form onSubmit={handleSubmit} className={styles.modalContentScrollable} style={{paddingRight: 5}}>
                
                {anexos.map((anexo, index) => (
                    <div key={index} style={{ marginBottom: '1.5rem', padding: '1rem', border: '1px solid #e0e0e0', borderRadius: '8px', background: '#fafafa', position: 'relative' }}>
                        
                        {/* Botão Remover Item */}
                        {anexos.length > 1 && (
                            <button type="button" onClick={() => removeAnexoForm(index)} className={styles.btnSecondary} style={{ position: 'absolute', top: 10, right: 10, color: '#c00' }} title="Remover">
                                <Trash2 size={18} />
                            </button>
                        )}

                        <div className={styles.gridForms}>
                            
                            {/* 1. TIPO (Dropdown Customizado) */}
                            <div>
                                <label className={styles.inputLabel}>Tipo</label>
                                <div className={styles.containerInputBusca} style={{ position: 'relative' }}>
                                    <input 
                                        className={styles.inputBusca}
                                        value={anexo.tipo}
                                        onChange={(e) => handleAnexoChange(index, "tipo", e.target.value)}
                                        onClick={(e) => { e.stopPropagation(); setTipoDropdownOpenIndex(index === tipoDropdownOpenIndex ? null : index); }}
                                        placeholder="Selecione ou digite..."
                                    />
                                    <ChevronDown size={14} style={{opacity: 0.5, position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none'}}/>
                                    
                                    {tipoDropdownOpenIndex === index && (
                                        <div className={styles.dropdownAbsoluto}>
                                            <div style={{maxHeight: 200, overflowY: 'auto'}}>
                                                {ANEXO_TIPOS_SUGESTOES.filter(t => t.toLowerCase().includes(anexo.tipo.toLowerCase())).map(t => (
                                                    <div key={t} className={styles.dropdownItem} onClick={() => { handleAnexoChange(index, "tipo", t); setTipoDropdownOpenIndex(null); }}>
                                                        {t}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 2. COMISSÕES (Se for Parecer) */}
                            {anexo.tipo.toLowerCase().includes("parecer") && (
                                <div>
                                    <label className={styles.inputLabel}><Users size={14} /> Comissões</label>
                                    <div className={styles.containerInputBusca} style={{height: 'auto', minHeight: 40, padding: 5, flexWrap: 'wrap', gap: 5}}>
                                        {comissoes.map(c => (
                                            <div 
                                                key={c.id} 
                                                onClick={() => toggleComissao(index, c.id)}
                                                style={{
                                                    padding: '4px 8px', 
                                                    borderRadius: 4, 
                                                    cursor: 'pointer',
                                                    border: '1px solid',
                                                    backgroundColor: anexo.comissaoIds.includes(c.id) ? '#e6f0ff' : '#fff',
                                                    borderColor: anexo.comissaoIds.includes(c.id) ? '#2b7cff' : '#ddd',
                                                    color: anexo.comissaoIds.includes(c.id) ? '#2b7cff' : '#555',
                                                    display: 'flex', alignItems: 'center', gap: 4
                                                }}
                                            >
                                                {c.sigla || c.nome.substring(0, 10)}
                                                {anexo.comissaoIds.includes(c.id) && <Check size={10} />}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* 3. MODO (Arquivo ou Texto) */}
                            <div style={{gridColumn: '1 / -1', display: 'flex', gap: 20, marginTop: 5}}>
                                <label style={{display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer'}}>
                                    <input type="radio" checked={anexo.modo === 'arquivo'} onChange={() => handleAnexoChange(index, 'modo', 'arquivo')} />
                                    <FileArchive size={18} /> Arquivo
                                </label>
                                <label style={{display: 'flex', alignItems: 'center', gap: 5, cursor: 'pointer'}}>
                                    <input type="radio" checked={anexo.modo === 'texto'} onChange={() => handleAnexoChange(index, 'modo', 'texto')} />
                                    <FileText size={18} /> Texto
                                </label>
                            </div>

                            {/* 4. CONTEÚDO (Arquivo ou Editor) */}
                            <div style={{gridColumn: '1 / -1', marginTop: 10}}>
                                {anexo.modo === 'arquivo' ? (
                                    <div className={styles.containerInputBusca} style={{flexDirection: 'column', alignItems: 'flex-start', gap: 10, padding: 10, height: 'auto'}}>
                                        
                                        {/* Botão Fake de Upload */}
                                        <label className={styles.pausarVotacao} style={{cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 5, padding: "0.7rem"}}>
                                            <Upload size={16}/> Uploard de Arquivo
                                            <input 
                                                type="file" 
                                                className={styles.uploadInputHidden} // Classe que esconde o input original
                                                style={{display: 'none'}} 
                                                onChange={(e) => handleFileChange(index, e.target.files?.[0])}
                                            />
                                        </label>

                                        {anexo.file && (
                                            <div style={{width: '100%', background: '#f0f0f0', padding: 10, borderRadius: 4}}>
                                                <div style={{ fontWeight: 'bold', marginBottom: 5}}>Selecionado: {anexo.file.name}</div>
                                                <label style={{display: 'block', marginBottom: 2}}>Nome (opcional):</label>
                                                <input 
                                                    className={styles.inputBusca} 
                                                    style={{background: 'white', height: 30}}
                                                    value={anexo.nomeDesejado}
                                                    onChange={(e) => handleAnexoChange(index, 'nomeDesejado', e.target.value)}
                                                    placeholder="Nome do arquivo no sistema..."
                                                />
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div style={{display: 'flex', flexDirection: 'column', gap: 10}}>
                                        <input 
                                            className={styles.inputBusca}
                                            placeholder="Título do texto (opcional)"
                                            value={anexo.tituloTexto}
                                            onChange={(e) => handleAnexoChange(index, 'tituloTexto', e.target.value)}
                                        />
                                        <div className={styles.containerInputBusca} style={{height: 'auto', minHeight: 200, padding: 0, overflow: 'hidden'}}>
                                            <TiptapEditor 
                                                value={anexo.texto} 
                                                onChange={(html) => handleAnexoChange(index, 'texto', html)}
                                                editable={true}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                        </div>
                    </div>
                ))}

                <button type="button" onClick={addAnexoForm} className={styles.pausarVotacao} style={{display: 'flex', alignItems: 'center', gap: 5, marginTop: 10}}>
                    <CirclePlus size={16} /> Adicionar outro item
                </button>

                <div className={styles.modalActions}>
                    <button type="submit" className={styles.iniciarVotacao}><CircleCheckBig size={18} /> Enviar</button>
                    <button type="button" onClick={closeModal} className={styles.encerrarVotacao}><SquareX size={18} /> Cancelar</button>
                </div>

                </form>
            </>
        )}
      </div>
    </div>
  );
};

export default ModalAdicionarAnexo;